import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateDebtAdjustmentRequest,
    CreatePaymentReceiptRequest,
    DebtTransactionsParams,
} from '@/lib/api/customer-debt-api'
import type { PaymentMethodChangeTarget } from '@/lib/api/sales-order-api'
import { customerDebtApi } from '@/lib/api/customer-debt-api'
import { ApiError } from '@/lib/api/client'
import { customerKeys } from '@/hooks/use-customer-query'
import { m } from '@/lib/paraglide/messages'
import { formatApiError } from '@/lib/format-api-error'
import { invalidateAfterMethodChange } from '@/hooks/use-sales-order-query'

export const customerDebtKeys = {
    all: ['customer-debt'] as const,
    transactions: (id: string, params: DebtTransactionsParams) =>
        [...customerDebtKeys.all, 'transactions', id, params] as const,
    receipt: (id: string, receiptId: string) =>
        [...customerDebtKeys.all, 'receipt', id, receiptId] as const,
}

// Chi tiết 1 phiếu thu (+ các đơn đã phân bổ) cho popup chứng từ. Chỉ fetch khi
// đã mở popup (có receiptId) để không gọi thừa.
export function useCustomerPaymentReceipt(id: string, receiptId?: string) {
    return useQuery({
        queryKey: customerDebtKeys.receipt(id, receiptId ?? ''),
        queryFn: async () => {
            const res = await customerDebtApi.getPaymentReceipt(
                id,
                receiptId as string,
            )
            return res.data
        },
        enabled: Boolean(id && receiptId),
    })
}

export function useCustomerDebtTransactions(
    id: string,
    params?: DebtTransactionsParams,
) {
    return useQuery({
        queryKey: customerDebtKeys.transactions(id, params ?? {}),
        queryFn: async () => {
            const res = await customerDebtApi.listTransactions(id, params)
            return { data: res.data ?? [], meta: res.meta }
        },
        enabled: Boolean(id),
        // Giữ dữ liệu trang trước khi đổi trang/filter → không nháy "Đang tải"
        // và không nhảy layout (chuẩn phân trang của repo).
        placeholderData: keepPreviousData,
    })
}

// Sau mọi biến động nợ: invalidate ledger + các query tổng hợp công nợ khách
// (detail current_debt, debts per-đơn, credit-status, list) để UI đồng bộ.
export function invalidateCustomerDebt(
    qc: ReturnType<typeof useQueryClient>,
    id: string,
) {
    void qc.invalidateQueries({ queryKey: customerDebtKeys.all })
    void qc.invalidateQueries({ queryKey: customerKeys.detail(id) })
    void qc.invalidateQueries({ queryKey: customerKeys.debts(id) })
    void qc.invalidateQueries({ queryKey: customerKeys.creditStatus(id) })
    void qc.invalidateQueries({ queryKey: customerKeys.lists() })
    // Báo cáo → Công nợ đọc cùng sổ: làm mới số liệu theo kỳ + sổ từng khách.
    void qc.invalidateQueries({ queryKey: ['reports', 'debt-period'] })
    void qc.invalidateQueries({ queryKey: ['reports', 'debt-ledger-period'] })
    void qc.invalidateQueries({ queryKey: ['reports', 'debt-summary'] })
}

export function useCreatePaymentReceipt(customerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreatePaymentReceiptRequest) =>
            customerDebtApi.createPaymentReceipt(customerId, data),
        onSuccess: () => {
            invalidateCustomerDebt(qc, customerId)
            toast.success(m.toast_customer_debt_payment_success())
        },
        onError: (err) => {
            // 409 duplicate_receipt: dialog tạo phiếu tự hiện confirm "vẫn
            // tạo?" — không toast lỗi ở đây kẻo chồng lên confirm.
            if (err instanceof ApiError && err.code === 'duplicate_receipt') {
                return
            }
            toast.error(m.toast_customer_debt_payment_error())
        },
    })
}

/**
 * Đổi hình thức cả phiếu thu (M-152): phiếu + mọi khoản phân bổ vào các đơn →
 * làm mới sổ công nợ, lịch sử thanh toán của mọi đơn và các báo cáo theo hình
 * thức.
 */
export function useChangePaymentReceiptMethod(customerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            receiptId,
            method,
        }: {
            receiptId: string
            method: PaymentMethodChangeTarget
        }) =>
            customerDebtApi.changePaymentReceiptMethod(
                customerId,
                receiptId,
                method,
            ),
        onSuccess: () => {
            invalidateAfterMethodChange(qc)
            invalidateCustomerDebt(qc, customerId)
            toast.success(m.order_payment_method_change_success())
        },
        onError: (err) => {
            const payload = formatApiError(
                err,
                m.order_payment_method_change_error(),
            )
            toast.error(payload.title, { description: payload.description })
            void qc.invalidateQueries({ queryKey: customerDebtKeys.all })
        },
    })
}

export function useCreateDebtAdjustment(customerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateDebtAdjustmentRequest) =>
            customerDebtApi.createAdjustment(customerId, data),
        onSuccess: () => {
            invalidateCustomerDebt(qc, customerId)
            toast.success(m.toast_customer_debt_adjust_success())
        },
        onError: () => toast.error(m.toast_customer_debt_adjust_error()),
    })
}

// Xóa 1 dòng công nợ (điều chỉnh / phiếu thu / thanh toán nhập tay). Lỗi 409
// mang message nghiệp vụ cụ thể từ server (dòng bị chặn, credit đã cấn…) —
// hiện nguyên văn thay vì message chung để người dùng biết vì sao không xóa được.
export function useDeleteDebtTransaction(customerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ txnId, reason }: { txnId: string; reason?: string }) =>
            customerDebtApi.deleteTransaction(customerId, txnId, reason),
        onSuccess: () => {
            invalidateCustomerDebt(qc, customerId)
            toast.success(m.toast_customer_debt_delete_success())
        },
        onError: (err) => {
            const msg =
                err instanceof ApiError &&
                (err.status === 409 || err.status === 404)
                    ? err.message
                    : m.toast_customer_debt_delete_error()
            toast.error(msg)
        },
    })
}

export function useAllocateCredit(customerId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (
            allocations?: CreatePaymentReceiptRequest['allocations'],
        ) => customerDebtApi.allocateCredit(customerId, { allocations }),
        onSuccess: () => {
            invalidateCustomerDebt(qc, customerId)
            toast.success(m.toast_customer_debt_allocate_success())
        },
        onError: () => toast.error(m.toast_customer_debt_allocate_error()),
    })
}
